"use client";

import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { type ReactNode, useEffect, useState } from "react";
import type { DeploymentEnvironment } from "@impakers/debug/telemetry";
import { useUserStore } from "@/stores/user-store";
import { FeedbackScrollUnlock } from "@/components/feedback-scroll-unlock";
import { TelemetryCollectors } from "@/components/telemetry-collectors";
import { PLACE_TELEMETRY_ENVIRONMENT, PLACE_TELEMETRY_SERVICE_ID, PLACE_TELEMETRY_SERVICE_NAME, setPlaceTelemetryDeploymentEnvironment } from "@/lib/telemetry/place-telemetry";
const ImpakersDebugProvider = dynamic(() => import("@impakers/debug/react").then(module => module.ImpakersDebugProvider));
const ImpakersFeedbackProvider = dynamic(() => import("local-feedbacker/react").then(module => module.ImpakersFeedbackProvider));
const TelemetryProvider = dynamic(() => import("@impakers/debug/telemetry/next").then(module => module.TelemetryProvider));

// 외부 공개/키오스크 성격의 경로에서는 피드배커(디버그 툴바)를 숨긴다.
const FEEDBACKER_HIDDEN_PREFIXES = ["/payments", "/attendance"];
export interface RuntimeInstrumentationProps {
  deploymentEnvironment: DeploymentEnvironment;
}

/**
 * 계측 도구는 제품 첫 paint와 무관하므로 hydration 뒤에만 provider chunk를 요청한다.
 *
 * TelemetryCollectors 가 쿼리 캐시·사용자 스토어를 구독하므로
 * QueryProvider·UserStoreProvider 안쪽에 둔다.
 */
export function RuntimeInstrumentation(t0) {
  const $ = _c(18);
  const {
    deploymentEnvironment
  } = t0;
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const user = useUserStore(_temp);
  let t1;
  let t2;
  if ($[0] !== deploymentEnvironment) {
    t1 = () => {
      setPlaceTelemetryDeploymentEnvironment(deploymentEnvironment);
    };
    t2 = [deploymentEnvironment];
    $[0] = deploymentEnvironment;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setMounted(true);
    };
    t4 = [];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  if (!mounted) {
    return null;
  }
  let t5;
  if ($[5] !== pathname) {
    t5 = FEEDBACKER_HIDDEN_PREFIXES.some(prefix => pathname?.startsWith(prefix));
    $[5] = pathname;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const showFeedbacker = !t5;
  let t6;
  if ($[7] !== deploymentEnvironment || $[8] !== showFeedbacker || $[9] !== user) {
    t6 = showFeedbacker && (deploymentEnvironment === "production" ? <><FeedbackScrollUnlock data-imp-o="app/runtime-instrumentation.tsx:78:13" /><ImpakersDebugProvider data-imp-o="app/runtime-instrumentation.tsx:79:13" endpoint="https://os.impakers.club/api/external/feedback" getUser={() => user ? {
        id: user.id,
        name: user.full_name
      } : null} /></> : <ImpakersFeedbackProvider data-imp-o="app/runtime-instrumentation.tsx:87:11" language="ko" namespace="place" />);
    $[7] = deploymentEnvironment;
    $[8] = showFeedbacker;
    $[9] = user;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = user?.id ?? null;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <TelemetryCollectors data-imp-o="app/runtime-instrumentation.tsx:96:9" />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== deploymentEnvironment || $[13] !== t7) {
    t9 = <TelemetryProvider data-imp-o="app/runtime-instrumentation.tsx:89:7" serviceId={PLACE_TELEMETRY_SERVICE_ID} serviceName={PLACE_TELEMETRY_SERVICE_NAME} environment={PLACE_TELEMETRY_ENVIRONMENT} deploymentEnvironment={deploymentEnvironment} userId={t7}>{t8}</TelemetryProvider>;
    $[12] = deploymentEnvironment;
    $[13] = t7;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t6 || $[16] !== t9) {
    t10 = <>{t6}{t9}</>;
    $[15] = t6;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
}
function _temp(state) {
  return state.user;
}